import { createIdentity } from "@alice-and-bot/core";
import { InstaQLEntity } from "@instantdb/react";
import { signal } from "@preact/signals";
import { posthog } from "posthog-js";
import { useEffect } from "preact/hooks";
import { toast } from "react-hot-toast";
import schema from "../../instant.schema.ts";
import { apiClient, refAttributionFor } from "../../constants.ts";
import { auth, transact, tx, useAuth, useQuery } from "./db.ts";
import {
  fireAnalyticsEventLegacy,
  referralStorageKey,
  safeLocalStorage,
  visitorStorageKey,
} from "./utils.ts";
import { trackSignUpConversion } from "./googleAnalyticsIntegration.ts";

const newUserParams = (name: string, now: number, attribution?: string) =>
  createIdentity(name).then((aliceAndBotCredentials) => ({
    aliceAndBotCredentials,
    lastVisit: now,
    ...(attribution ? { attribution } : {}),
  }));

// signupTimestamp is write-once (enforced by InstantDB permissions): only set
// it when the existing record doesn't have one yet.
const signupTimestampParam = (
  existing: { signupTimestamp?: number | null } | undefined,
  now: number,
) => (existing?.signupTimestamp ? {} : { signupTimestamp: now });

const creationLock = signal(false);

const pendingAuthLock = signal(false);

const tokenLoginLock = signal(false);

const authTransitionInFlight = () =>
  pendingAuthLock.value || tokenLoginLock.value;

export const ensureUser = () => {
  const auth = useAuth();

  const { data: activeUserData, isLoading: activeUserLoading } = useQuery({
    $users: {
      $: { where: { id: auth.user?.id ?? "" } },
    },
  });
  const activeUser = activeUserData?.$users?.[0];

  const email = auth.user?.email;

  useEffect(() => {
    if (activeUser?.id) posthog.identify(activeUser?.id);
    if (email) {
      posthog.setPersonProperties({ email });
    }
  }, [activeUser?.id, email]);

  useEffect(() => {
    if (activeUser?.id) {
      transact([tx.$users[activeUser.id].update({ lastVisit: Date.now() })])
        .catch(
          (e: unknown) => {
            console.error("Failed to update last visit:", e);
          },
        );
    }
  }, [activeUser?.id]);

  useEffect(() => {
    if (!auth.user?.id) return;
    if (activeUserLoading) return;
    if (activeUser?.aliceAndBotCredentials) return;
    if (authTransitionInFlight()) return;
    if (activeUser?.pendingAuthToken) return;

    console.log("Creating user details for auth id:", auth.user.id);
    if (!creationLock.value) {
      creationLock.value = true;
      const refId = safeLocalStorage.getItem(referralStorageKey);
      const attribution = refId ? refAttributionFor(refId) : undefined;
      const now = Date.now();
      newUserParams(auth.user?.email ?? "anonymous", now, attribution)
        .then((params) => {
          return auth.user?.id
            ? transact([
              tx.$users[auth.user.id].update({
                ...params,
                ...signupTimestampParam(activeUser, now),
              }),
            ]).then(() => {
              fireAnalyticsEventLegacy("signin_completed");
              posthog.capture("Signin Completed");
              trackSignUpConversion("email");
              const previousVisitorId =
                safeLocalStorage.getItem(visitorStorageKey) ?? undefined;
              apiClient({
                endpoint: "report-signup-telemetry",
                payload: {
                  userId: auth.user.id,
                  attribution,
                  previousVisitorId,
                },
              }).catch(() => {});
            }).catch((e: unknown) => {
              console.error(
                "Failed to create user details:",
                { authId: auth.user?.id, error: String(e), raw: e },
              );
              posthog.capture("Signin Error", {
                type: "create_user",
                error: String(e),
              });
              toast.error("Sign up failed, please try again.");
              creationLock.value = false;
            })
            : Promise.resolve().then(() => {
              creationLock.value = false;
            });
        }).catch((e) => {
          console.error(
            "Failed to generate new user params (e.g. createIdentity failed):",
            e,
          );
          posthog.capture("Signin Error", {
            type: "new_user_params",
            error: String(e),
          });
          toast.error("Sign up setup failed, please try again.");
          creationLock.value = false;
        });
    }
  }, [
    creationLock.value,
    activeUser?.id,
    auth.user?.id,
    activeUserLoading,
    activeUser?.pendingAuthToken,
    pendingAuthLock.value,
    tokenLoginLock.value,
  ]);
};

export const useActiveUser = (): // deno-lint-ignore ban-types
| InstaQLEntity<typeof schema, "$users", {}>
| undefined => {
  const auth = useAuth();
  const { data, error } = useQuery({
    $users: {
      $: { where: { id: auth.user?.id ?? "" } },
    },
  });
  if (error) console.error(error);
  return data?.$users?.[0];
};

const extractTokenParam = () => {
  const params = new URLSearchParams(globalThis.location?.search ?? "");
  return params.get("token");
};

const clearTokenParam = () => {
  const url = new URL(globalThis.location.href);
  url.searchParams.delete("token");
  globalThis.history.replaceState({}, "", url.toString());
};

export const useTokenLogin = () => {
  const token = typeof globalThis.location !== "undefined"
    ? extractTokenParam()
    : null;
  useEffect(() => {
    if (!token || tokenLoginLock.value) return;
    tokenLoginLock.value = true;
    auth.signInWithToken(token).then(clearTokenParam).catch((e: unknown) => {
      console.error("Failed to sign in with token param:", e);
      toast.error("Login link expired or invalid.");
    }).finally(() => {
      tokenLoginLock.value = false;
    });
  }, [token]);
};

export const usePendingAuthToken = () => {
  const { user } = useAuth();
  const { data } = useQuery({
    $users: {
      $: { where: { id: user?.id ?? "" } },
    },
  });
  const appUser = data?.$users?.[0];
  const pendingToken = appUser?.pendingAuthToken;
  const appUserId = appUser?.id;
  useEffect(() => {
    if (!pendingToken || !appUserId || pendingAuthLock.value) return;
    pendingAuthLock.value = true;
    auth.signInWithToken(pendingToken).then(() =>
      transact([tx.$users[appUserId].update({ pendingAuthToken: "" })])
    ).catch((e: unknown) => {
      console.error("Failed to sign in with pending auth token:", e);
    }).finally(() => {
      pendingAuthLock.value = false;
    });
  }, [pendingToken, appUserId]);
};
